import { Box, Skeleton, Typography, styled } from '@mui/material';
import React from 'react';

import { ChartFrame } from 'components/charts/chart-frame';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartSurfaceProvider } from 'contexts/chart/chart-surface-context';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import { ChartSurfaceProps } from 'components/charts/types/chart-surface.type';

type Props = ChartSurfaceProps & {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  height?: number;
  loading?: boolean;
  isEmpty?: boolean;
  emptyState?: React.ReactNode;
  children: React.ReactNode;
};

const Root = styled(ChartFrame)(({ theme }) => ({
  gap: theme.spacing(4),
}));

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: chartPalette.text.primary,
}));

const Subtitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 14,
  fontWeight: 400,
  color: chartPalette.text.secondary,
}));

const LoadingSkeleton = styled(Skeleton, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  backgroundColor: chartPalette.backgrounds.raised,
}));

const Body = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const ChartCard: React.FC<Props> = ({
  title,
  subtitle,
  actions,
  footer,
  height = 320,
  loading,
  isEmpty,
  emptyState,
  children,
  dark,
}) => {
  const chartPalette = useChartPalette(dark);
  const animationDuration = useChartAnimation() ? 0 : 160;

  // Charts, legends, and empty states inside the card follow its `dark` value.
  return (
    <ChartSurfaceProvider dark={dark}>
      <Root chartPalette={chartPalette} animationDuration={animationDuration}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={4}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Title chartPalette={chartPalette} variant="h3">
              {title}
            </Title>
            {subtitle && <Subtitle chartPalette={chartPalette}>{subtitle}</Subtitle>}
          </Box>
          {actions && (
            <Box display="flex" alignItems="center" gap={2}>
              {actions}
            </Box>
          )}
        </Box>

        <Box minHeight={height} flexGrow={1} display="flex" flexDirection="column">
          {loading ? (
            <LoadingSkeleton chartPalette={chartPalette} variant="rounded" width="100%" height={height} />
          ) : isEmpty ? (
            emptyState
          ) : (
            <Body>{children}</Body>
          )}
        </Box>

        {footer && !loading && !isEmpty && <Box>{footer}</Box>}
      </Root>
    </ChartSurfaceProvider>
  );
};

export default ChartCard;
